/**
 * Integration Parser Utility
 * Parses integration documentation from conversation content
 */

/**
 * Deduplicates integrations by title (case-insensitive)
 */
const deduplicateIntegrations = (integrations) => {
  const uniqueIntegrations = [];
  const seenTitles = new Set();

  integrations.forEach(integration => {
    const normalizedTitle = integration.title.toLowerCase().trim();
    if (!seenTitles.has(normalizedTitle)) {
      seenTitles.add(normalizedTitle);
      uniqueIntegrations.push(integration);
    } else {
      console.log('⚠️ Skipping duplicate integration:', integration.title);
    }
  });

  return uniqueIntegrations;
};

/**
 * Extracts integration documentation from message content
 * Looks for patterns like:
 * ## Google Maps API
 * 1. Google Maps API
 * 5. Places API - Place Details
 *
 * @param {string} content - The message content to parse
 * @returns {Array} Array of integration objects
 */
export const extractIntegrations = (content) => {
  if (!content) return [];

  const integrations = [];

  // FIRST PRIORITY: Check for "Integration N:" pattern (most explicit format)
  // Find all occurrences of "Integration N:" in the content
  const integrationPatternMatches = [];
  const integrationRegex = /(?:^|\n)(?:Integration|Int\.?)\s+(\d+)\s*:\s*([^\n]+)/gi;
  let match;

  while ((match = integrationRegex.exec(content)) !== null) {
    let title = match[2]
      .replace(/\*+/g, '')  // Remove asterisks
      .replace(/\s+/g, ' ')  // Normalize whitespace
      .replace(/\([^)]*\)/g, '')  // Remove parenthetical notes like (CRITICAL)
      .replace(/🔴|⚠️|✅|❌|⭐/g, '')  // Remove emoji indicators
      .trim();

    // Find the line start index (accounting for optional newline)
    const lineStart = match[0].startsWith('\n') ? match.index + 1 : match.index;

    integrationPatternMatches.push({
      index: lineStart,
      fullMatch: match[0].trim(),
      number: match[1],
      title: title,
      lineEnd: match.index + match[0].length
    });
  }

  // If we found "Integration N:" patterns, use them
  if (integrationPatternMatches.length > 0) {
    console.log('✅ Found integration patterns:', integrationPatternMatches.length);

    integrationPatternMatches.forEach((patternMatch, idx) => {
      const startIndex = patternMatch.lineEnd;
      const endIndex = idx < integrationPatternMatches.length - 1
        ? integrationPatternMatches[idx + 1].index
        : content.length;

      let sectionContent = content.substring(startIndex, endIndex).trim();

      // Remove trailing lists of future integrations (common pattern at the end)
      // Look for phrases like "Once you provide details", "I'll move to", "Next integrations:"
      const futureListPatterns = [
        /Once\s+you\s+provide[\s\S]*?(?=\n\n|$)/gi,
        /I'll\s+move\s+to[\s\S]*?(?=\n\n|$)/gi,
        /Next\s+integrations?:[\s\S]*?(?=\n\n|$)/gi,
        /What\s+are\s+the\s+specifics[\s\S]*?$/gi
      ];

      futureListPatterns.forEach(pattern => {
        sectionContent = sectionContent.replace(pattern, '').trim();
      });

      // Skip if content is too short
      if (sectionContent.length < 10) {
        console.log(`⚠️ Skipping "${patternMatch.title}" - content too short`);
        return;
      }

      integrations.push({
        id: `INT${String(integrations.length + 1).padStart(3, '0')}`,
        title: patternMatch.title,
        rawContent: sectionContent,
        ...parseIntegrationDetails(sectionContent)
      });
    });

    if (integrations.length > 0) {
      console.log('✅ Parsed integrations using "Integration N:" pattern');
      return deduplicateIntegrations(integrations);
    }
  }

  // SECOND PRIORITY: Extract Table of Contents to use as source of truth
  const tocMatch = content.match(/(?:Table of Contents|Contents)([\s\S]*?)(?=\n#{1,2}\s+\d+\.|\n\d+\.\s+[A-Z])/i);
  let tocSections = [];

  if (tocMatch) {
    // Extract section names and numbers from TOC
    const tocContent = tocMatch[1];
    const tocLines = tocContent.split('\n');
    tocLines.forEach(line => {
      // Match "1. Section Name" or "1.1 Section Name" or "[Section Name](...)"
      // Support both "1." and "1.1" style numbers
      const numMatch = line.match(/^\s*(\d+(?:\.\d+)?)[.\s]+(.+?)(?:\s*$|\s*\()/);
      if (numMatch) {
        const sectionNumber = numMatch[1]; // Keep as string to preserve "1.1" format
        let sectionTitle = numMatch[2].trim();
        // Remove markdown link syntax if present
        sectionTitle = sectionTitle.replace(/\[([^\]]+)\].*$/, '$1').trim();
        tocSections.push({ number: sectionNumber, title: sectionTitle });
      }
    });
  }

  // If we found TOC sections, use them to find content sections
  if (tocSections.length > 0) {
    const foundIntegrations = [];

    tocSections.forEach((tocSection, idx) => {
      // Build regex to find this exact section in content
      // Match: "1. Section Name" or "1.1 Section Name" or "## 1. Section Name" or "## Section Name"
      const escapedTitle = tocSection.title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const escapedNumber = tocSection.number.toString().replace(/\./g, '\\.');
      const sectionRegex = new RegExp(
        `^(?:#{1,2}\\s+)?${escapedNumber}[.\\s]+${escapedTitle}\\s*$`,
        'mi'
      );

      const match = content.match(sectionRegex);
      if (match) {
        const startIndex = content.indexOf(match[0]) + match[0].length;

        // Find end index (start of next TOC section or end of content)
        let endIndex = content.length;
        if (idx < tocSections.length - 1) {
          const nextSection = tocSections[idx + 1];
          const nextEscapedNumber = nextSection.number.toString().replace(/\./g, '\\.');
          const nextRegex = new RegExp(
            `^(?:#{1,2}\\s+)?${nextEscapedNumber}[.\\s]+${nextSection.title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*$`,
            'mi'
          );
          const nextMatch = content.match(nextRegex);
          if (nextMatch) {
            endIndex = content.indexOf(nextMatch[0]);
          }
        }

        const sectionContent = content.substring(startIndex, endIndex).trim();

        // Skip if content is too short or mostly links
        // Reduced threshold to 10 chars to include more sections
        if (sectionContent.length < 10) {
          console.log(`⚠️ Skipping TOC section "${tocSection.title}" - content too short (${sectionContent.length} chars)`);
          return;
        }

        const linkMatches = sectionContent.match(/\[([^\]]+)\]\(#[^\)]+\)/g);
        const hasMultipleLinks = linkMatches && linkMatches.length > 3;
        const contentWithoutLinks = sectionContent.replace(/\[([^\]]+)\]\(#[^\)]+\)/g, '').trim();
        const isMainlyLinks = hasMultipleLinks && contentWithoutLinks.length < sectionContent.length * 0.3;

        if (isMainlyLinks) return;

        foundIntegrations.push({
          id: `INT${String(foundIntegrations.length + 1).padStart(3, '0')}`,
          title: tocSection.title,
          rawContent: sectionContent,
          ...parseIntegrationDetails(sectionContent)
        });
      }
    });

    if (foundIntegrations.length > 0) {
      console.log('✅ Found integrations using TOC parsing:', foundIntegrations.length);
      // Return early - don't run fallback parsing to avoid duplicates
      return deduplicateIntegrations(foundIntegrations);
    }
  }

  console.log('⚠️ TOC parsing found no integrations, trying fallback parsing...');

  // Fallback: If no TOC found or TOC parsing failed, use old logic

  // Find main section headers
  // Strategy: Look for patterns that indicate a main section header, not content lists
  const lines = content.split('\n');
  const mainMatches = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const prevLine = i > 0 ? lines[i - 1].trim() : '';
    const nextLine = i < lines.length - 1 ? lines[i + 1].trim() : '';

    // Match ## headers or numbered headers
    const headerMatch = line.match(/^(#{1,2}\s+)?(\d+)?\.\s*(.+)$/);

    if (!headerMatch) continue;

    const hasHashPrefix = !!headerMatch[1];
    const number = headerMatch[2];
    const title = headerMatch[3].trim();
    const titleLower = title.toLowerCase();

    // Exclude meta sections
    if (titleLower.includes('table of contents') ||
        titleLower.includes('executive summary') ||
        titleLower === 'contents') {
      continue;
    }

    // Keep ## headers (markdown headers)
    if (hasHashPrefix) {
      mainMatches.push({
        index: content.indexOf(line, mainMatches.length > 0 ? mainMatches[mainMatches.length - 1].index + 1 : 0),
        title: title,
        fullMatch: line,
        isMainSection: true
      });
      continue;
    }

    // For numbered items, distinguish between main sections and content lists
    if (number) {
      // Indicators of a MAIN section header:
      // 1. Has format like "2.1" (subsection notation)
      // 2. OR preceded by blank line or start of document
      // 3. OR title starts with capital letter and is not ending with colon
      // 4. OR title matches known section patterns

      const hasSubsectionFormat = line.match(/^\d+\.\d+\s+/);
      const precededByBlankLine = prevLine === '';
      const startsWithCapital = title[0] === title[0].toUpperCase();
      const endsWithColon = title.endsWith(':');
      const followedByNonList = nextLine && !nextLine.match(/^[-*\d]\s*[\[\(]/);

      // Known section-like patterns
      const sectionPatterns = [
        /assessment/i,
        /framework/i,
        /management/i,
        /implementation/i,
        /security/i,
        /protection/i,
        /authentication/i,
        /network/i,
        /monitoring/i,
        /response/i,
        /compliance/i,
        /checklist/i,
        /policies/i
      ];

      const matchesSectionPattern = sectionPatterns.some(pattern => pattern.test(title));

      // It's a main section if:
      // - Has subsection format (2.1, 3.2, etc.) - these are main sections
      // - OR (preceded by blank line AND starts with capital AND matches section pattern)
      // - OR (preceded by blank line AND doesn't end with colon AND followed by non-list)
      const isMainSection = hasSubsectionFormat ||
                            (precededByBlankLine && startsWithCapital && matchesSectionPattern) ||
                            (precededByBlankLine && !endsWithColon && followedByNonList && startsWithCapital);

      // Additional checks to filter out content lists:
      const numberValue = parseInt(number);

      // 1. If number is > 20, it's likely a content list
      if (numberValue > 20) {
        continue;
      }

      // 2. If it ends with colon and number is small (1-10), it's likely a content subheading
      // unless it has subsection format (2.1, 3.2, etc.)
      if (endsWithColon && numberValue <= 10 && !hasSubsectionFormat) {
        // Check if it's NOT a main section by looking at context
        // If the previous main section was found recently (within 100 chars), this is likely content
        const lastMainSectionIndex = mainMatches.length > 0 ? mainMatches[mainMatches.length - 1].index : -1;
        const currentLineIndex = content.indexOf(line);
        const distanceFromLastSection = currentLineIndex - lastMainSectionIndex;

        if (distanceFromLastSection < 500 && lastMainSectionIndex !== -1) {
          // Too close to previous section, likely a content subheading
          continue;
        }
      }

      if (isMainSection) {
        mainMatches.push({
          index: content.indexOf(line, mainMatches.length > 0 ? mainMatches[mainMatches.length - 1].index + 1 : 0),
          title: title,
          fullMatch: line,
          isMainSection: true
        });
      }
    }
  }

  mainMatches.forEach((match, index) => {
    // Extract the title from the match object
    let title = match.title;

    // Remove leading number from title if present (e.g., "1. Title" -> "Title", "2.1 Title" -> "Title")
    // But keep the original if removal would leave empty/whitespace-only title
    const titleWithoutNumber = title.replace(/^\d+(\.\d+)?\s*/, '').trim();
    if (titleWithoutNumber.length > 0) {
      title = titleWithoutNumber;
    }
    // Otherwise keep the original title with numbers

    const startIndex = match.index + match.fullMatch.length;
    const endIndex = index < mainMatches.length - 1
      ? mainMatches[index + 1].index
      : content.length;

    let integrationContent = content.substring(startIndex, endIndex).trim();

    // Skip if content is too short (likely a section header only)
    // Reduced threshold to 10 chars to include more sections
    if (integrationContent.length < 10) {
      console.log(`⚠️ Skipping "${title}" - content too short (${integrationContent.length} chars)`);
      return;
    }

    // Skip if content is mostly markdown links (indicating it's a navigation/index section)
    const linkMatches = integrationContent.match(/\[([^\]]+)\]\(#[^\)]+\)/g);
    const hasMultipleLinks = linkMatches && linkMatches.length > 3;
    const contentWithoutLinks = integrationContent.replace(/\[([^\]]+)\]\(#[^\)]+\)/g, '').trim();
    const isMainlyLinks = hasMultipleLinks && contentWithoutLinks.length < integrationContent.length * 0.3;

    if (isMainlyLinks) {
      // This is likely a navigation/index section, skip it
      return;
    }

    // Parse integration details
    const integration = {
      id: `INT${String(integrations.length + 1).padStart(3, '0')}`,
      title: title,
      rawContent: integrationContent,
      ...parseIntegrationDetails(integrationContent)
    };

    integrations.push(integration);
  });

  // NEW FORMAT FALLBACK: If still no integrations found, try the new recommendation format
  // Format: ## Integration Name (without numbers)
  if (integrations.length === 0) {
    console.log('📋 Trying new format parser (## headers without TOC)');

    // Find all ## level headers (h2)
    const h2Regex = /^##\s+(.+)$/gm;
    const h2Matches = [];
    let match;

    while ((match = h2Regex.exec(content)) !== null) {
      h2Matches.push({
        title: match[1].trim(),
        index: match.index,
        fullMatch: match[0]
      });
    }

    console.log('Found', h2Matches.length, 'h2 headers');

    h2Matches.forEach((h2Match, idx) => {
      const startIndex = h2Match.index + h2Match.fullMatch.length;
      const endIndex = idx < h2Matches.length - 1
        ? h2Matches[idx + 1].index
        : content.length;

      const sectionContent = content.substring(startIndex, endIndex).trim();

      console.log('📄 Extracted section:', h2Match.title);
      console.log('📄 Section content preview:', sectionContent.substring(0, 200));
      console.log('📄 Newlines in section:', (sectionContent.match(/\n/g) || []).length);

      // Skip if content is too short
      // Reduced threshold to 10 chars to include more sections
      if (sectionContent.length < 10) {
        console.log('Skipping', h2Match.title, '- too short (', sectionContent.length, 'chars)');
        return;
      }

      // Skip meta sections
      const titleLower = h2Match.title.toLowerCase();
      if (titleLower.includes('table of contents') ||
          titleLower.includes('executive summary') ||
          titleLower === 'contents' ||
          titleLower.includes('summary') ||
          titleLower.includes('introduction')) {
        console.log('Skipping', h2Match.title, '- meta section');
        return;
      }

      console.log('✅ Adding integration:', h2Match.title);

      integrations.push({
        id: `INT${String(integrations.length + 1).padStart(3, '0')}`,
        title: h2Match.title,
        rawContent: sectionContent,
        ...parseIntegrationDetails(sectionContent)
      });
    });

    // If h2 parsing found integrations, return early with deduplication
    if (integrations.length > 0) {
      console.log('✅ Found integrations using h2 parsing:', integrations.length);
      const uniqueIntegrations = deduplicateIntegrations(integrations);
      console.log('Final integrations count (after dedup):', uniqueIntegrations.length);
      return uniqueIntegrations;
    }
  }

  console.log('Final integrations count (before dedup):', integrations.length);

  // Deduplicate and return
  const uniqueIntegrations = deduplicateIntegrations(integrations);

  console.log('Final integrations count (after dedup):', uniqueIntegrations.length);
  return uniqueIntegrations;
};

/**
 * Parses integration details from content
 * Extracts: type, description, authentication, endpoints, configuration, etc.
 * Enhanced to handle multiple option format
 */
export const parseIntegrationDetails = (content) => {
  const details = {
    type: 'API',
    authType: null,
    description: '',
    endpoints: [],
    configuration: {},
    credentials: {},
    documentation: '',
    testUrl: '',
    implementation: '',
    status: 'pending',
    options: [],  // Array of integration options
    whatToImplement: [],  // Scope of Implementation (array of features)
    implementationApproaches: []  // New: Array of implementation approaches
  };

  // Check if content has multiple options (new format)
  const hasOptions = /###\s+Option\s+\d+:/i.test(content);

  if (hasOptions) {
    // Parse multiple options format
    // DON'T extract description for integrations with options
    // The description/context is part of the overall integration markdown
    // and showing it separately causes duplication
    details.description = '';

    // Extract all options
    const optionRegex = /###\s+Option\s+\d+:\s*(.+?)(?=###\s+Option|\n---|\n##|$)/gis;
    const optionMatches = [...content.matchAll(optionRegex)];

    optionMatches.forEach((match, idx) => {
      const optionContent = match[0];
      const optionTitle = match[1].split('\n')[0].trim();

      const option = {
        title: optionTitle,
        type: extractField(optionContent, 'Type'),
        auth: extractField(optionContent, 'Auth'),
        logic: extractLogicSteps(optionContent),
        pros: extractField(optionContent, 'Pros'),
        cons: extractField(optionContent, 'Cons'),
        setup: extractField(optionContent, 'Setup'),
        isRecommended: /\(recommended\)/i.test(optionTitle)
      };

      details.options.push(option);

      // Use first option's auth for backward compatibility
      if (idx === 0 && option.auth) {
        details.authType = detectAuthType(option.auth);
      }
    });

    // Use recommended option's details if available
    const recommendedOption = details.options.find(opt => opt.isRecommended);
    if (recommendedOption) {
      if (recommendedOption.auth) details.authType = detectAuthType(recommendedOption.auth);
      if (recommendedOption.setup) {
        details.configuration = parseSetupToConfig(recommendedOption.setup);
      }
    }
  } else {
    // Original parsing logic for old format
    // Extract authentication type
    const authPatterns = {
      'oauth': /\b(oauth|oauth2|oauth 2\.0|authorization code|implicit flow)\b/i,
      'bearer': /\b(bearer token|api token|access token)\b/i,
      'apikey': /\b(api key|api-key|apikey)\b/i,
      'basic': /\b(basic auth|username.*password|credentials)\b/i
    };

    for (const [type, pattern] of Object.entries(authPatterns)) {
      if (pattern.test(content)) {
        details.authType = type;
        break;
      }
    }

    // Extract description (first paragraph or text before first bold section)
    const descMatch = content.match(/^([^*#`\n]+?)(?=\n\*\*|\n##|\n```|$)/s);
    if (descMatch) {
      details.description = descMatch[1].trim();
    }
  }

  // Extract endpoints (look for URL patterns)
  const urlRegex = /https?:\/\/[^\s\n<>]+/g;
  const urls = content.match(urlRegex) || [];
  details.endpoints = [...new Set(urls)].filter(url =>
    !url.includes('documentation') && !url.includes('docs')
  );

  // Extract test URL
  const testUrlMatch = content.match(/test\s+url[:\s]+([^\n]+)/i);
  if (testUrlMatch) {
    details.testUrl = testUrlMatch[1].trim();
  }

  // Extract configuration sections
  const configMatch = content.match(/\*\*Configuration[:\s]*\*\*([\s\S]*?)(?=\n\*\*|\n##|$)/i);
  if (configMatch) {
    details.configuration = parseKeyValuePairs(configMatch[1]);
  }

  // Extract credentials/keys
  const credMatch = content.match(/\*\*(?:Credentials|API Keys?|Tokens?)[:\s]*\*\*([\s\S]*?)(?=\n\*\*|\n##|$)/i);
  if (credMatch) {
    details.credentials = parseKeyValuePairs(credMatch[1]);
  }

  // Extract implementation code
  const codeMatch = content.match(/```[\s\S]*?```/);
  if (codeMatch) {
    details.implementation = codeMatch[0].replace(/```(\w+)?\n?/g, '').trim();
  }

  // Extract documentation link
  const docMatch = content.match(/\[documentation\]\((https?:\/\/[^)]+)\)/i) ||
                   content.match(/documentation[:\s]+(https?:\/\/[^\s\n]+)/i);
  if (docMatch) {
    details.documentation = docMatch[1];
  }

  // Extract "What We Are Going to Implement" section
  const whatToImplementMatch = content.match(/##\s+What\s+We\s+Are\s+Going\s+to\s+Implement\s*\n([\s\S]*?)(?=\n##|$)/i);
  if (whatToImplementMatch) {
    const whatContent = whatToImplementMatch[1].trim();
    // Extract bullet points (features to implement)
    const featureRegex = /^[*\-]\s+(.+?)$/gm;
    let featureMatch;
    while ((featureMatch = featureRegex.exec(whatContent)) !== null) {
      details.whatToImplement.push(featureMatch[1].trim());
    }
  }

  // Extract "Implementation Approaches" section
  const approachesMatch = content.match(/##\s+Implementation\s+Approaches\s*\n([\s\S]*?)(?=\n##\s+(?!#)|$)/i);
  if (approachesMatch) {
    const approachesContent = approachesMatch[1].trim();

    // Extract each approach (### Approach N:)
    const approachRegex = /###\s+Approach\s+\d+:\s*(.+?)\n([\s\S]*?)(?=###\s+Approach\s+\d+:|$)/gi;
    let approachMatch;

    while ((approachMatch = approachRegex.exec(approachesContent)) !== null) {
      const approachTitle = approachMatch[1].trim();
      const approachContent = approachMatch[2].trim();

      const approach = {
        name: approachTitle,
        explanation: extractMarkdownField(approachContent, 'Explanation'),
        codeRequirements: extractBulletPoints(approachContent, 'Code Requirements'),
        advantages: extractBulletPoints(approachContent, 'Advantages'),
        bestFor: extractMarkdownField(approachContent, 'Best For'),
        isRecommended: false
      };

      details.implementationApproaches.push(approach);
    }

    // Extract recommended approach
    const recommendedMatch = approachesContent.match(/\*\*Recommended\s+Approach:\*\*\s*(.+?)(?=\n##|$)/is);
    if (recommendedMatch && details.implementationApproaches.length > 0) {
      const recommendedText = recommendedMatch[1].toLowerCase();

      // Try to identify which approach is recommended
      details.implementationApproaches.forEach((approach, idx) => {
        const approachNumber = idx + 1;
        if (recommendedText.includes(`approach ${approachNumber}`) ||
            recommendedText.includes(approach.name.toLowerCase())) {
          approach.isRecommended = true;
        }
      });

      // Store full recommendation text
      details.recommendedApproach = recommendedMatch[1].trim();
    }
  }

  return details;
};

/**
 * Parses key-value pairs from text
 * Example: "Client ID: xyz123\nClient Secret: abc456"
 */
const parseKeyValuePairs = (text) => {
  const pairs = {};
  const lines = text.split('\n');

  lines.forEach(line => {
    const match = line.match(/^[*\s-]*([^:]+):\s*(.+)$/);
    if (match) {
      const key = match[1].trim().replace(/\*\*/g, '');
      const value = match[2].trim().replace(/\*\*/g, '');
      if (key && value) {
        pairs[key] = value;
      }
    }
  });

  return pairs;
};

/**
 * Extracts a field value from option content
 * Example: "**Type:** SaaS API" -> "SaaS API"
 */
const extractField = (content, fieldName) => {
  const regex = new RegExp(`\\*\\*${fieldName}:\\*\\*\\s*(.+?)(?=\\n\\*\\*|\\n###|$)`, 'is');
  const match = content.match(regex);
  return match ? match[1].trim() : '';
};

/**
 * Extracts integration logic steps
 * Example: "1. Step one\n2. Step two" -> ["Step one", "Step two"]
 */
const extractLogicSteps = (content) => {
  const logicMatch = content.match(/\*\*Integration Logic:\*\*([\s\S]*?)(?=\n\*\*|\n###|$)/i);
  if (!logicMatch) return [];

  const logicText = logicMatch[1].trim();
  const steps = [];

  // Match numbered steps
  const stepRegex = /^\d+\.\s+(.+?)$/gm;
  let match;
  while ((match = stepRegex.exec(logicText)) !== null) {
    steps.push(match[1].trim());
  }

  return steps;
};

/**
 * Detects authentication type from auth string
 */
const detectAuthType = (authString) => {
  const authLower = authString.toLowerCase();
  if (authLower.includes('oauth')) return 'oauth';
  if (authLower.includes('bearer') || authLower.includes('token')) return 'bearer';
  if (authLower.includes('api key')) return 'apikey';
  if (authLower.includes('basic')) return 'basic';
  return null;
};

/**
 * Extracts a markdown field value
 * Example: "**Explanation:** Some text here" -> "Some text here"
 */
const extractMarkdownField = (content, fieldName) => {
  const regex = new RegExp(`\\*\\*${fieldName}:\\*\\*\\s*(.+?)(?=\\n\\*\\*|\\n###|$)`, 'is');
  const match = content.match(regex);
  return match ? match[1].trim() : '';
};

/**
 * Extracts bullet points from a markdown section
 * Example:
 * **Code Requirements:**
 * - Item 1
 * - Item 2
 * Returns: ["Item 1", "Item 2"]
 */
const extractBulletPoints = (content, sectionName) => {
  const regex = new RegExp(`\\*\\*${sectionName}:\\*\\*\\s*\\n([\\s\\S]*?)(?=\\n\\*\\*|\\n###|$)`, 'i');
  const match = content.match(regex);

  if (!match) return [];

  const sectionContent = match[1].trim();
  const bulletPoints = [];
  const bulletRegex = /^[*\-]\s+(.+?)$/gm;
  let bulletMatch;

  while ((bulletMatch = bulletRegex.exec(sectionContent)) !== null) {
    bulletPoints.push(bulletMatch[1].trim());
  }

  return bulletPoints;
};

/**
 * Parses setup instructions to configuration object
 * Example: "`STRIPE_SECRET_KEY`, `STRIPE_PUBLISHABLE_KEY`" -> {STRIPE_SECRET_KEY: "", STRIPE_PUBLISHABLE_KEY: ""}
 */
const parseSetupToConfig = (setupText) => {
  const config = {};
  const envVarRegex = /`([A-Z_]+)`/g;
  let match;
  while ((match = envVarRegex.exec(setupText)) !== null) {
    config[match[1]] = '';
  }
  return config;
};

/**
 * Formats integration type display name
 */
export const formatIntegrationType = (type) => {
  const typeMap = {
    'api': 'API Integration',
    'oauth': 'OAuth 2.0',
    'webhook': 'Webhook',
    'sdk': 'SDK Integration',
    'service': 'Service Integration'
  };

  return typeMap[type?.toLowerCase()] || type || 'Integration';
};

/**
 * Formats authentication type display name
 */
export const formatAuthType = (authType) => {
  const authMap = {
    'oauth': 'OAuth 2.0',
    'bearer': 'Bearer Token',
    'apikey': 'API Key',
    'basic': 'Basic Auth'
  };

  return authMap[authType?.toLowerCase()] || authType || 'Not specified';
};

/**
 * Gets icon/badge color for auth type
 */
export const getAuthTypeBadgeColor = (authType) => {
  const colorMap = {
    'oauth': 'primary',
    'bearer': 'success',
    'apikey': 'info',
    'basic': 'warning'
  };

  return colorMap[authType?.toLowerCase()] || 'default';
};

/**
 * Formats integration for clipboard copy
 */
export const formatIntegrationForCopy = (integration) => {
  let text = `${integration.title}\n`;
  text += '='.repeat(integration.title.length) + '\n\n';

  // Always prefer rawContent if available, as it contains the complete, unmodified content
  if (integration.rawContent) {
    text += integration.rawContent + '\n';
    return text;
  }

  // Fallback to structured format if no rawContent
  if (integration.description) {
    text += `Description: ${integration.description}\n\n`;
  }

  // Add "What We Are Going to Implement" if available
  if (integration.whatToImplement && integration.whatToImplement.length > 0) {
    text += `What We Are Going to Implement:\n`;
    integration.whatToImplement.forEach(feature => {
      text += `  - ${feature}\n`;
    });
    text += '\n';
  }

  // Add Implementation Approaches if available
  if (integration.implementationApproaches && integration.implementationApproaches.length > 0) {
    text += `Implementation Approaches:\n\n`;
    integration.implementationApproaches.forEach((approach, idx) => {
      text += `Approach ${idx + 1}: ${approach.name}${approach.isRecommended ? ' (Recommended)' : ''}\n`;
      if (approach.explanation) {
        text += `  Explanation: ${approach.explanation}\n`;
      }
      if (approach.codeRequirements && approach.codeRequirements.length > 0) {
        text += `  Code Requirements:\n`;
        approach.codeRequirements.forEach(req => {
          text += `    - ${req}\n`;
        });
      }
      if (approach.advantages && approach.advantages.length > 0) {
        text += `  Advantages:\n`;
        approach.advantages.forEach(adv => {
          text += `    - ${adv}\n`;
        });
      }
      if (approach.bestFor) {
        text += `  Best For: ${approach.bestFor}\n`;
      }
      text += '\n';
    });
    if (integration.recommendedApproach) {
      text += `Recommended: ${integration.recommendedApproach}\n\n`;
    }
  }

  if (integration.authType) {
    text += `Authentication: ${formatAuthType(integration.authType)}\n\n`;
  }

  if (integration.endpoints && integration.endpoints.length > 0) {
    text += `Endpoints:\n`;
    integration.endpoints.forEach(endpoint => {
      text += `  - ${endpoint}\n`;
    });
    text += '\n';
  }

  if (integration.configuration && Object.keys(integration.configuration).length > 0) {
    text += `Configuration:\n`;
    Object.entries(integration.configuration).forEach(([key, value]) => {
      text += `  ${key}: ${value}\n`;
    });
    text += '\n';
  }

  if (integration.credentials && Object.keys(integration.credentials).length > 0) {
    text += `Credentials:\n`;
    Object.entries(integration.credentials).forEach(([key, value]) => {
      text += `  ${key}: ${value}\n`;
    });
    text += '\n';
  }

  if (integration.implementation) {
    text += `Implementation:\n\`\`\`\n${integration.implementation}\n\`\`\`\n\n`;
  }

  if (integration.documentation) {
    text += `Documentation: ${integration.documentation}\n`;
  }

  return text;
};

/**
 * Reconstructs integration markdown from parsed data
 */
export const reconstructIntegrationMarkdown = (integrations) => {
  let markdown = '';

  integrations.forEach((integration, index) => {
    if (index > 0) markdown += '\n\n---\n\n';

    // Add header using "Integration N: Title" format (matches system prompt)
    markdown += `Integration ${index + 1}: ${integration.title}\n\n`;

    // Add content
    if (integration.rawContent) {
      markdown += integration.rawContent;
    }
  });

  return markdown.trim();
};
